import Markdown from '@scale/frontend-lib/markdown';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
// TODO: Import @material-ui from design-system folder
/* eslint-disable restrict-imports/restrict-imports */
import { Button, Popper, Popover } from '@material-ui/core';
import {
  CategoryChoice,
  CategoryChoiceLongHint,
  ChoiceLongHintField,
} from '@scale/shared/types/params/textcollection';
import React, { Dispatch, SetStateAction } from 'react';
import { makeStyles } from 'design-system/styles';
import { Checkbox } from 'design-system';
import { MOBILE_THRESHOLD } from '@scale/frontend-lib/util';
import classNames from 'classnames';
import _ from 'lodash';
// eslint-disable-next-line import-path/forbidden
import ChevronUpIcon from 'react-scale/components/icons/ChevronUp';
// eslint-disable-next-line import-path/forbidden
import ChevronDownIcon from 'react-scale/components/icons/ChevronDown';
import ReactPlayer from 'react-player';
import { useToggle } from 'react-use-v13';

interface ChoiceLongHintAndWorkflowProps {
  // For both
  anchorRef: any;

  // For LongHints
  longHints: CategoryChoiceLongHint[];
  isOpen: boolean;

  // For Check Your Work (workflow)
  workflows: string[];
  choice: CategoryChoice | undefined;
  onClose: (isWorkflowComplete: boolean) => void;
}

export const ChoiceLongHintAndWorkflow: React.FC<ChoiceLongHintAndWorkflowProps> = props => {
  const classes = useCategorizationFieldSectionStyles();

  const hintHasContent = (longhint: ChoiceLongHintField) => {
    return !(_.isEmpty(longhint.text?.trim()) && _.isEmpty(longhint.imageUrl?.trim()));
  };

  const handleClose = () => {
    props.onClose(false);
  };

  const handleButtonClick = (workflowComplete: boolean) => {
    props.onClose(workflowComplete);
  };

  return (
    <Popover
      anchorEl={props.anchorRef.current}
      anchorOrigin={{
        vertical: 'top',
        horizontal: 'center',
      }}
      transformOrigin={{
        vertical: 'top',
        horizontal: 'center',
      }}
      open={props.isOpen}
    >
      {/* Directly including responsive styles since this component is only rendered when responsive upstream */}
      <div className={classNames(classes.workflowHints, classes.workflowHintsResponsive)}>
        <div>
          <Button className={classes.closeButton} onClick={handleClose}>
            <FontAwesomeIcon icon="times" />
          </Button>
          <h3>Hints and/or Check Your Work</h3>
        </div>
        {/* CHECK YOUR WORK */}
        {!_.isEmpty(props.workflows) && (
          <CheckYourWorkContent
            workflows={props.workflows}
            onButtonClick={handleButtonClick}
            choice={props.choice}
          />
        )}
        {/* LONG HINTS */}
        {!_.isEmpty(props.longHints) && (
          <LongHintContent
            // Only rendered when responsive upstream
            responsive={true}
            longHints={props.longHints}
            hintHasContent={hintHasContent}
            style={{ marginTop: '16px' }}
          />
        )}
      </div>
    </Popover>
  );
};

interface ChoiceLongHintProps {
  longHints: ChoiceLongHintField[];
  isOpen: boolean;
  anchorRef: any;
  previousLongHints?: CategoryChoiceLongHint[];
  childrenLongHints?: CategoryChoiceLongHint[];
  setLongHints?: Dispatch<SetStateAction<CategoryChoiceLongHint[]>>;
  setPreviousLongHints?: Dispatch<SetStateAction<CategoryChoiceLongHint[]>>;
  setChildrenLongHints?: Dispatch<SetStateAction<CategoryChoiceLongHint[]>>;
}

export const ChoiceLongHint: React.FC<ChoiceLongHintProps> = props => {
  const hintHasContent = (longhint: ChoiceLongHintField) => {
    return !(_.isEmpty(longhint.text?.trim()) && _.isEmpty(longhint.imageUrl?.trim()));
  };

  const [isHidden, setIsHidden] = React.useState<boolean>(false);

  return (
    <Popper
      open={props.isOpen}
      placement={'left-start'}
      anchorEl={props.anchorRef.current}
      modifiers={{
        flip: { enabled: false },
        offset: {
          enabled: true,
          offset: '0, 40px',
        },
      }}
    >
      <LongHintContent
        responsive={false}
        longHints={props.longHints}
        hintHasContent={hintHasContent}
        isHidden={isHidden}
        toggleHidden={() => setIsHidden(!isHidden)}
        previousLongHints={props.previousLongHints}
        childrenLongHints={props.childrenLongHints}
        setLongHints={props.setLongHints}
        setPreviousLongHints={props.setPreviousLongHints}
        setChildrenLongHints={props.setChildrenLongHints}
      />
    </Popper>
  );
};

interface LongHintContentProps {
  responsive: boolean;
  longHints: ChoiceLongHintField[];
  hintHasContent: (longHint: ChoiceLongHintField) => boolean;
  isHidden?: boolean;
  toggleHidden?: () => void;
  style?: React.CSSProperties;
  previousLongHints?: CategoryChoiceLongHint[];
  childrenLongHints?: CategoryChoiceLongHint[];
  setLongHints?: Dispatch<SetStateAction<CategoryChoiceLongHint[]>>;
  setPreviousLongHints?: Dispatch<SetStateAction<CategoryChoiceLongHint[]>>;
  setChildrenLongHints?: Dispatch<SetStateAction<CategoryChoiceLongHint[]>>;
}

export const LongHintContent: React.FC<LongHintContentProps> = props => {
  const classes = useCategorizationFieldSectionStyles();

  const anyHints: boolean = _.some(props.longHints.map(hint => props.hintHasContent(hint)));

  const handleClickHints = (hints: CategoryChoiceLongHint[], previous: boolean = false) => {
    if (props.setLongHints && props.setPreviousLongHints && props.setChildrenLongHints) {
      props.setLongHints(hints);
      props.setPreviousLongHints(props.longHints as CategoryChoiceLongHint[]);
      setPreviousChildrenLongHints(props.childrenLongHints ?? []);
      props.setChildrenLongHints(previous ? previousChildrenLongHints : []);
    }
  };

  const [expandChildren, toggleExpandChildren] = useToggle(false);
  const [previousChildrenLongHints, setPreviousChildrenLongHints] = React.useState<
    CategoryChoiceLongHint[]
  >([]);

  const toggleHiddenFn = props.toggleHidden;
  return (
    <div
      className={classNames(classes.longHints, {
        [classes.longHintsResponsive]: props.responsive,
      })}
      style={props.style}
    >
      {anyHints && !_.isNil(toggleHiddenFn) ? (
        <Button variant="text" size="small" onClick={() => toggleHiddenFn()}>
          {props.isHidden ? (
            <ChevronUpIcon width="24" height="24" />
          ) : (
            <ChevronDownIcon width="24" height="24" />
          )}{' '}
        </Button>
      ) : undefined}
      {props.isHidden ?? false
        ? undefined
        : props.longHints.map((longHint, index) => {
            return props.hintHasContent(longHint) ? (
              <div className={classes.longHintSection} key={index}>
                {props.previousLongHints?.length ? (
                  <a onClick={() => handleClickHints(props.previousLongHints ?? [], true)}>
                    Previous Hint
                  </a>
                ) : (
                  <></>
                )}
                {longHint.title ? <h4>{longHint.title}</h4> : null}
                {longHint.text ? (
                  <p dangerouslySetInnerHTML={{ __html: Markdown.renderInline(longHint.text) }} />
                ) : null}
                {longHint.imageUrl ? (
                  <img src={`${longHint.imageUrl}`} className={classes.longHintSectionImage} />
                ) : null}
                {longHint.videoUrls && longHint.videoUrls.length > 0 ? (
                  <HintVideoExamples videoUrls={longHint.videoUrls} />
                ) : null}
                {props.childrenLongHints?.length ? (
                  <p>
                    <a onClick={toggleExpandChildren}>
                      {expandChildren ? 'Hide' : 'See'} Children Hints
                    </a>
                  </p>
                ) : null}
                {expandChildren && props.childrenLongHints?.length
                  ? props.childrenLongHints.map(c => (
                      <div key={c.title}>
                        {c.text ? (
                          <a onClick={() => handleClickHints([c])}>{c.title}</a>
                        ) : (
                          <div>{c.title}</div>
                        )}
                      </div>
                    ))
                  : null}
              </div>
            ) : null;
          })}
    </div>
  );
};

interface ChoiceWorkflowProps {
  workflows: string[];
  choice: CategoryChoice | undefined;
  anchorRef: any;
  onClose: (isWorkflowComplete: boolean) => void;
}

export const ChoiceWorkflow: React.FC<ChoiceWorkflowProps> = props => {
  const classes = useCategorizationFieldSectionStyles();

  const handleClose = () => {
    props.onClose(false);
  };
  const handleButtonClick = (workflowComplete: boolean) => {
    props.onClose(workflowComplete);
  };

  return (
    <Popover
      anchorEl={props.anchorRef.current}
      anchorOrigin={{
        vertical: 'top',
        horizontal: 'center',
      }}
      transformOrigin={{
        vertical: 'top',
        horizontal: 'center',
      }}
      open={true}
    >
      <div className={classes.workflowHints}>
        <div>
          <Button className={classes.closeButton} onClick={handleClose}>
            <FontAwesomeIcon icon="times" />
          </Button>
          <h3>Check Your Work</h3>
        </div>
        <CheckYourWorkContent
          workflows={props.workflows}
          onButtonClick={handleButtonClick}
          choice={props.choice}
        />
      </div>
    </Popover>
  );
};

interface CheckYourWorkContentProps {
  workflows: string[];
  onButtonClick: (workflowComplete: boolean) => void;
  choice: CategoryChoice | undefined;
}

const CheckYourWorkContent: React.FC<CheckYourWorkContentProps> = props => {
  const classes = useCategorizationFieldSectionStyles();

  const uncheckedWorkflows = () => {
    return Object.fromEntries(_.map(_.keys(props.workflows), e => [e, false]));
  };

  const [checkedWorkflows, setCheckedWorkflows] = React.useState<Record<string, boolean>>(
    uncheckedWorkflows(),
  );

  const [isWorkflowComplete, setIsWorkflowComplete] = React.useState<boolean>(false);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const nextCheckedWorkflows = { ...checkedWorkflows, [e.target.name]: e.target.checked };
    setIsWorkflowComplete(_.every(_.values(nextCheckedWorkflows)));
    setCheckedWorkflows(nextCheckedWorkflows);
  };

  const handleButtonClick = () => {
    setCheckedWorkflows(uncheckedWorkflows());
    props.onButtonClick(isWorkflowComplete);
  };

  let buttonCopy = `Select ${_.isNil(props.choice) ? 'Choice' : props.choice.label}`;
  if (buttonCopy.length > 70) {
    buttonCopy = 'Select Choice';
  }
  return (
    <>
      <p> Check all the boxes to confirm your work and select the node</p>
      <div>
        {props.workflows.map((workflow, index) => {
          return (
            <Checkbox
              className={classes.workflowCheckbox}
              label={workflow}
              name={index.toString()}
              key={index}
              checked={checkedWorkflows[index]}
              onChange={handleChange}
            />
          );
        })}
      </div>
      <br />
      <Button
        color="primary"
        size="small"
        fullWidth={true}
        variant="text"
        onClick={handleButtonClick}
        disabled={!isWorkflowComplete}
      >
        {buttonCopy}
      </Button>
    </>
  );
};

const HintVideoExamples: React.FC<$FIXME> = ({ videoUrls }) => {
  const [index, setIndex] = React.useState<number>(0);
  const onLeft = React.useCallback(() => {
    setIndex(_.clamp(index - 1, 0, videoUrls.length - 1));
  }, [videoUrls.length, index]);

  const onRight = React.useCallback(() => {
    setIndex(_.clamp(index + 1, 0, videoUrls.length - 1));
  }, [videoUrls.length, index]);

  const classes = useCategorizationFieldSectionStyles();

  return (
    <>
      Examples:
      <div className={classes.video}>
        <ReactPlayer controls width="100%" url={videoUrls[index]} />
      </div>
      <div className={classes.videoControls}>
        <Button onClick={onLeft} disabled={index === 0}>
          Prev
        </Button>
        <Button onClick={onRight} disabled={index === videoUrls.length - 1}>
          Next
        </Button>
      </div>
    </>
  );
};

const useCategorizationFieldSectionStyles = makeStyles(theme => {
  return {
    workflowHints: {
      alignItems: 'center',
      background: theme.colors.white,
      padding: '10px',
      border: `1px solid ${theme.colors.gold}`,
      borderRadius: 8,
      flexGrow: 1,
      maxWidth: '350px',
      maxHeight: '700px',
      overflow: 'scroll',
    },
    workflowHintsResponsive: {
      [`@media (max-width: ${MOBILE_THRESHOLD})`]: {
        maxHeight: 'initial',
        overflow: 'initial',
      },
    },
    closeButton: {
      background: 'white',
      padding: 4,
      borderRadius: 24,
      width: 24,
      height: 24,
      display: 'flex',
      flexDirection: 'row',
      alignItems: 'center',
      justifyContent: 'center',
      border: 'none',
      cursor: 'pointer',
      float: 'right',
    },
    longHints: {
      display: 'inline-block',
      alignItems: 'center',
      lineHeight: '20px',
      background: theme.colors.white,
      padding: '10px',
      border: `1px solid ${theme.colors.green}`,
      borderRadius: 8,
      flexGrow: 1,
      maxWidth: '350px',
      maxHeight: '700px',
      overflow: 'scroll',
    },
    longHintsHideButton: {
      display: 'inline-block',
      alignItems: 'center',
      lineHeight: '20px',
      background: theme.colors.white,
      padding: '10px',
      border: `1px solid ${theme.colors.green}`,
      borderRadius: 8,
      flexGrow: 1,
    },
    longHintsResponsive: {
      [`@media (max-width: ${MOBILE_THRESHOLD})`]: {
        maxHeight: 'initial',
        overflow: 'initial',
      },
    },
    longHintSection: {
      border: `1px solid ${theme.colors.gray20}`,
      padding: '7px',
      margin: '7px',
    },
    longHintSectionImage: {
      maxHeight: '250px',
    },
    selectButton: {
      width: '100%',
    },
    workflowCheckbox: {
      padding: '10px',
    },
    video: {
      marginTop: 8,
    },
    videoControls: {
      marginTop: 8,
      display: 'flex',
      flexDirection: 'row',
      justifyContent: 'space-between',
    },
  };
});
